import React, { useState } from 'react';
import { RefreshCw, CheckCircle, XCircle, AlertCircle, Image, FolderX, Settings } from 'lucide-react';

function SyncButtonMejorado() {
  const [syncing, setSyncing] = useState(false);
  const [syncingImages, setSyncingImages] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const [imageResult, setImageResult] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Sincronización de paquetes (mantener original)
  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/sync-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResult(data.results);
      } else {
        setError(data.message || 'Error en la sincronización');
      }
    } catch (err) {
      setError('Error de conexión: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  // ✨ NUEVA: Sincronización mejorada de imágenes
  const handleSyncImages = async () => {
    setSyncingImages(true);
    setImageResult(null);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/sync-image-mappings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setImageResult(data);
      } else {
        setError(data.message || 'Error sincronizando mapeos de imágenes');
      }
    } catch (err) {
      setError('Error sincronizando imágenes: ' + err.message);
    } finally {
      setSyncingImages(false);
    }
  };

  // ✨ NUEVA: Verificar carpetas de imágenes
  const handleVerifyFolders = async () => {
    setVerifying(true);
    setVerifyResult(null);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/verify-image-folders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setVerifyResult(data);
      } else {
        setError('Error verificando carpetas');
      }
    } catch (err) {
      setError('Error verificando carpetas: ' + err.message);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SINCRONIZACIÓN DE PAQUETES (ORIGINAL) */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Sincronizar Paquetes y Descuentos
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Actualiza paquetes y descuentos Family Plan desde Opciones Argentinas
            </p>
          </div>
          
          <button
            onClick={handleSync}
            disabled={syncing}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
              syncing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#2AA8B0] hover:bg-[#1E8A91] text-white'
            }`}
          >
            <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar Paquetes'}
          </button>
        </div>

        {/* Resultado de sincronización de paquetes (mantener original) */}
        {result && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-green-900">Sincronización exitosa</h4>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-white rounded p-3">
                <p className="text-gray-600">Nuevos</p>
                <p className="text-2xl font-bold text-green-600">{result.paquetesNuevos}</p>
              </div>
              
              <div className="bg-white rounded p-3">
                <p className="text-gray-600">Actualizados</p>
                <p className="text-2xl font-bold text-blue-600">{result.paquetesActualizados}</p>
              </div>
              
              <div className="bg-white rounded p-3">
                <p className="text-gray-600">Desactivados</p>
                <p className="text-2xl font-bold text-orange-600">{result.paquetesDesactivados}</p>
              </div>
              
              <div className="bg-white rounded p-3">
                <p className="text-gray-600">👨‍👩‍👧‍👦 Family Plan</p>
                <p className="text-2xl font-bold text-purple-600">{result.familyPlanActualizados || 0}</p>
              </div>
            </div>
            
            {result.errores && result.errores.length > 0 && (
              <div className="mt-3 p-3 bg-yellow-50 rounded">
                <p className="text-sm text-yellow-800">
                  <AlertCircle className="inline w-4 h-4 mr-1" />
                  {result.errores.length} error(es) durante la sincronización
                </p>
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-3">
              Última sincronización: {new Date(result.timestamp).toLocaleString('es-AR')}
            </p>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SINCRONIZACIÓN DE MAPEOS DE IMÁGENES (MEJORADO) */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Image className="w-5 h-5" />
              Sincronizar Mapeos de Imágenes
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Mapea destinos a carpetas y cuenta imágenes reales automáticamente
            </p>
          </div>
          
          <div className="flex gap-2">
            {/* Botón de verificación */}
            <button
              onClick={handleVerifyFolders}
              disabled={verifying}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-semibold transition-colors ${
                verifying
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              }`}
              title="Verificar todas las carpetas de imágenes"
            >
              <Settings className={`w-5 h-5 ${verifying ? 'animate-spin' : ''}`} />
              {verifying ? 'Verificando...' : 'Verificar'}
            </button>
            
            {/* Botón principal de sincronización */}
            <button
              onClick={handleSyncImages}
              disabled={syncingImages}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                syncingImages
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              <Image className={`w-5 h-5 ${syncingImages ? 'animate-pulse' : ''}`} />
              {syncingImages ? 'Sincronizando...' : 'Sincronizar'}
            </button>
          </div>
        </div>

        {/* ✨ NUEVO: Resultado de sincronización mejorado */}
        {imageResult && (
          <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-purple-900">
                {imageResult.mensaje || 'Mapeos sincronizados'}
              </h4>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div className="bg-white rounded p-3">
                <p className="text-gray-600">Total</p>
                <p className="text-2xl font-bold text-purple-600">{imageResult.totalDestinos}</p>
              </div>
              
              <div className="bg-white rounded p-3">
                <p className="text-gray-600">Nuevos</p>
                <p className="text-2xl font-bold text-green-600">{imageResult.nuevos}</p>
              </div>
              
              <div className="bg-white rounded p-3">
                <p className="text-gray-600">Actualizados</p>
                <p className="text-2xl font-bold text-blue-600">{imageResult.actualizados}</p>
              </div>
              
              <div className="bg-white rounded p-3">
                <p className="text-gray-600">🔧 Corregidos</p>
                <p className="text-2xl font-bold text-amber-600">{imageResult.corregidos || 0}</p>
              </div>
              
              <div className="bg-white rounded p-3">
                <p className="text-gray-600">Sin Mapeo</p>
                <p className="text-2xl font-bold text-orange-600">
                  {Array.isArray(imageResult.sinMapeo) ? imageResult.sinMapeo.length : 0}
                </p>
              </div>
            </div>
            
            {/* Destinos sin mapeo */}
            {imageResult.sinMapeo && Array.isArray(imageResult.sinMapeo) && imageResult.sinMapeo.length > 0 && (
              <div className="mt-3 p-3 bg-orange-50 rounded border border-orange-200">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-orange-800">
                    ⚠️ Destinos sin mapeo ({imageResult.sinMapeo.length}):
                  </p>
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-xs text-orange-600 hover:text-orange-800 underline"
                  >
                    {showDetails ? 'Ocultar' : 'Ver todos'}
                  </button>
                </div>
                
                <div className="text-xs text-orange-700 space-y-1">
                  {(showDetails ? imageResult.sinMapeo : imageResult.sinMapeo.slice(0, 5)).map(destino => (
                    <div key={destino}>• {destino}</div>
                  ))}
                  {!showDetails && imageResult.sinMapeo.length > 5 && (
                    <div className="italic text-orange-600">
                      ... y {imageResult.sinMapeo.length - 5} más
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-orange-600 mt-2">
                  💡 Estos destinos usarán fuzzy matching automático para encontrar carpetas similares
                </p>
              </div>
            )}
            
            {/* Carpetas faltantes */}
            {imageResult.carpetasNoExisten && imageResult.carpetasNoExisten.length > 0 && (
              <div className="mt-3 p-3 bg-red-50 rounded border border-red-200">
                <p className="text-sm font-medium text-red-800 mb-2">
                  <FolderX className="inline w-4 h-4 mr-1" />
                  Carpetas faltantes ({imageResult.carpetasNoExisten.length}):
                </p>
                <div className="text-xs text-red-700 space-y-1">
                  {imageResult.carpetasNoExisten.slice(0, 5).map((item, i) => (
                    <div key={i}>• {item.destino} → {item.carpeta}</div>
                  ))}
                  {imageResult.carpetasNoExisten.length > 5 && (
                    <div className="italic">
                      ... y {imageResult.carpetasNoExisten.length - 5} más
                    </div>
                  )}
                </div>
                <p className="text-xs text-red-600 mt-2">
                  ❌ Crear estas carpetas en /public/images/destinos/
                </p>
              </div>
            )}
          </div>
        )}

        {/* ✨ NUEVO: Resultado de verificación de carpetas */}
        {verifyResult && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-blue-900">
                📁 Carpetas Verificadas: {verifyResult.total}
              </h4>
              <button
                onClick={() => setVerifyResult(null)}
                className="text-blue-600 hover:text-blue-800"
              >
                ✕
              </button>
            </div>
            
            <div className="max-h-60 overflow-y-auto space-y-1 text-sm">
              {verifyResult.carpetas.map((carpeta, i) => (
                <div
                  key={i}
                  className={`flex justify-between p-2 rounded ${
                    carpeta.estado === 'OK' ? 'bg-white' : 'bg-red-100'
                  }`}
                >
                  <span className="font-mono text-xs">{carpeta.carpeta}</span>
                  <span className={`font-semibold ${
                    carpeta.estado === 'OK' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {carpeta.cantidad > 0 ? `${carpeta.cantidad} imgs` : 'VACÍA'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error global */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800 font-semibold">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default SyncButtonMejorado;